import * as React from 'react';
import * as DateFns from 'date-fns';
import { AddressInfo } from '../types/addressTypes';
import RadioGroupInput, { RadioOptions } from './RadioGroupInput';
import AddressCard from './AddressCard';
import Calendar from './Calendar';
import ActionButtons from './ActionButtons';
import '../css/appointment-selection.sass';
import formatter from '../utils/formatter-util';

type AddressInfoType = AddressInfo;
type RadioOptionsType = RadioOptions;

interface Slot {
  date: string;
  timeSlot: string;
  code: string;
  available?: boolean;
  day?: string;
}

interface Appointment {
  day: string;
  date: string;
  slots: Slot[];
}

interface AppointmentSelectionProps {
  address: AddressInfoType;
  availableAppointments: Appointment[];
  closeAppointmentPopup?: Function;
  scheduleInstallationAppointment: Function;
  savedInstallationDetails?: Slot;
  appointmentSelectionLabel?: string;
  addressLabel?: string;
  scheduledInstallationLabel?: string;
  firstAvailableInstallationLabel?: string;
  availableInstallationDatesLabel?: string;
  availableInstallationTimeLabel?: string;
  appointmentSelectionDisclaimerContent?: React.ReactNode;
  appointmentSelectionSummaryContent?: React.ReactNode;
  cancelBtnText?: string;
  scheduleInstallBtnText?: string;
  theme?: 'night' | 'day';
  showAdditionalMonth?: boolean;
  appointmentFeeLbl?: string;
  appointmentFee?: number;
  isDivider?: boolean;
  addressCardHeading?: string;
  showHeaderLabel?: boolean;
  defaultTimeSlotSeleted?: boolean;
  hasTncDisclaimer?: boolean;
  tncSelected?: boolean;
  isProcessing?: boolean;
}

const formattedDate = (dateStr: string) => {
  const newDate = new Date(dateStr.replace(/-/g, '/'));
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const formattedTime = (timeStr: string) => {
  let newStr = timeStr.replace(':00', '').replace(':00', '');
  newStr = newStr.replace('-0', '-').replace('-', ' - ');
  if (timeStr.charAt(0) === '0') {
    newStr = newStr.replace('0', '');
  }
  return newStr;
};

export default function AppointmentSelection(props: AppointmentSelectionProps) {
  const {
    theme = 'day',
    appointmentSelectionLabel = 'Schedule Install',
    addressLabel = 'Installation Address',
    scheduledInstallationLabel = 'Scheduled Installation:',
    firstAvailableInstallationLabel = 'First Available Installation:',
    availableInstallationDatesLabel = 'Available Install Dates',
    availableInstallationTimeLabel = 'Available Install Times',
    scheduleInstallBtnText = 'Schedule Install',
    appointmentFeeLbl = 'Appointment Fee',
    showHeaderLabel = true,
    defaultTimeSlotSeleted = true
  } = props;

  const savedInstallationDate =
    props.savedInstallationDetails?.date &&
    DateFns.format(
      formattedDate(props.savedInstallationDetails?.date),
      'yyyy-MM-dd'
    );

  const availableAppointments = props.availableAppointments.filter(
    (appointment) => {
      let dateAvailable = false;
      appointment.slots.forEach((slot) => {
        if (slot.available) {
          dateAvailable = true;
        }
      });
      return dateAvailable;
    }
  );

  const [selectedInstallation, setSelectedInstallation] = React.useState<Slot>({
    date: props.savedInstallationDetails?.date
      ? DateFns.format(
          formattedDate(props.savedInstallationDetails?.date),
          'yyyy-MM-dd'
        )
      : availableAppointments[0].date,
    timeSlot: props.savedInstallationDetails?.timeSlot
      ? props.savedInstallationDetails?.timeSlot
      : (defaultTimeSlotSeleted &&
          availableAppointments[0].slots.filter(
            (availableSlot) => availableSlot.available
          )[0].timeSlot) ||
        '',
    code: props.savedInstallationDetails?.date
      ? availableAppointments &&
        availableAppointments
          .filter(
            (appointment: Appointment) =>
              DateFns.format(formattedDate(appointment.date), 'yyyy-MM-dd') ===
              savedInstallationDate
          )[0]
          .slots.filter((availableSlot) => availableSlot.available)[0].code
      : availableAppointments[0].slots.filter(
          (availableSlot) => availableSlot.available
        )[0].code
  });

  const timeSlotOptions = (): RadioOptionsType[] => {
    if (selectedInstallation.date) {
      const selectedAppointment = availableAppointments.filter(
        (appointment: Appointment) =>
          DateFns.format(formattedDate(appointment.date), 'yyyy-MM-dd') ===
          DateFns.format(
            formattedDate(selectedInstallation.date || ''),
            'yyyy-MM-dd'
          )
      )[0];
      return selectedAppointment.slots.map((slot) => {
        return {
          label: formattedTime(slot.timeSlot.toUpperCase()),
          value: slot.timeSlot,
          disabled: !slot.available
        };
      });
    }
    return availableAppointments[0].slots.map((slot) => {
      return {
        label: slot.timeSlot.toUpperCase(),
        value: slot.timeSlot,
        disabled: !slot.available
      };
    });
  };

  const datesBetween = (start: Date, end: Date) => {
    const dateArr = [];
    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
      dateArr.push(DateFns.format(dt, 'yyyy-MM-dd'));
    }
    return dateArr;
  };

  const dateOrdinals = (num: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  const installationDateFormat = () => {
    const date = props.savedInstallationDetails?.date
      ? availableAppointments.filter(
          (appointment: Appointment) =>
            DateFns.format(formattedDate(appointment.date), 'yyyy-MM-dd') ===
            savedInstallationDate
        )[0].date
      : availableAppointments[0].date;
    const { day } = availableAppointments.filter(
      (appointment: Appointment) => appointment.date === date
    )[0];
    return `${day.slice(0, 3)}, ${DateFns.format(
      formattedDate(date),
      'MMM d'
    )}${dateOrdinals(formattedDate(date).getDate())}`;
  };

  let maxDate = formattedDate(
    availableAppointments[availableAppointments.length - 1].date
  );
  const minDate = formattedDate(availableAppointments[0].date);

  if (props.showAdditionalMonth && maxDate < DateFns.addMonths(new Date(), 1)) {
    maxDate = DateFns.addMonths(new Date(), 1);
  }

  const unAvailableDates = () => {
    const appointmentDates = availableAppointments.map(
      (appointment: Appointment) => {
        const date = formattedDate(appointment.date);
        return DateFns.format(date, 'yyyy-MM-dd');
      }
    );
    return datesBetween(minDate, maxDate)
      .filter((dateStr: string) => appointmentDates.indexOf(dateStr) === -1)
      .map((dateStr: string) => formattedDate(dateStr));
  };

  const onDateSelect = (date: Date) => {
    const selectedAppointment =
      date &&
      availableAppointments.filter(
        (appointment: Appointment) =>
          DateFns.format(date, 'yyyy-MM-dd') ===
          DateFns.format(formattedDate(appointment.date), 'yyyy-MM-dd')
      )[0];
    setSelectedInstallation({
      date: DateFns.format(
        formattedDate(selectedAppointment.date),
        'yyyy-MM-dd'
      ),
      timeSlot: '',
      code: ''
    });
  };

  const onTimeSelect = (selectedSlot: string) => {
    const optedTimeSlot = availableAppointments
      .filter(
        (opDate: Appointment) =>
          DateFns.format(formattedDate(opDate.date), 'yyyy-MM-dd') ===
          DateFns.format(formattedDate(selectedInstallation.date), 'yyyy-MM-dd')
      )[0]
      .slots.filter((opSlot: Slot) => opSlot.timeSlot === selectedSlot)[0];
    setSelectedInstallation({
      ...selectedInstallation,
      timeSlot: optedTimeSlot.timeSlot,
      code: optedTimeSlot.code
    });
  };

  return (
    <div className={`appointment-details ${theme}`}>
      {showHeaderLabel && <h1>{appointmentSelectionLabel}</h1>}
      <div className="col-md-12 appointment-details-container">
        <div className="col-md-4">
          <h2>{addressLabel}</h2>
          <div className="address-container">
            <AddressCard
              heading={props.addressCardHeading}
              address={props.address}
            />
          </div>
          <h3>
            {props.savedInstallationDetails
              ? scheduledInstallationLabel
              : firstAvailableInstallationLabel}
          </h3>
          {availableAppointments[0] && availableAppointments[0] && (
            <>
              <div className="first-available-installation">
                <div className="column-title">Date</div>
                <div className="column-value">{installationDateFormat()}</div>
              </div>
              <div className="first-available-installation">
                <div className="column-title">Time</div>
                <div className="column-value">
                  {props.savedInstallationDetails
                    ? formattedTime(
                        availableAppointments
                          .filter(
                            (slotDate: Appointment) =>
                              DateFns.format(
                                formattedDate(slotDate.date),
                                'yyyy-MM-dd'
                              ) === savedInstallationDate
                          )[0]
                          .slots.filter(
                            (availableSlot) =>
                              availableSlot.timeSlot ===
                              props.savedInstallationDetails?.timeSlot
                          )[0]
                          .timeSlot.toUpperCase()
                      )
                    : formattedTime(
                        availableAppointments[0].slots
                          .filter((availableSlot) => availableSlot.available)[0]
                          .timeSlot.toUpperCase()
                      )}
                </div>
              </div>
              {props.appointmentFee && (
                <div className="first-available-installation">
                  <div className="column-title">{appointmentFeeLbl}</div>
                  <div className="column-value">
                    {formatter({
                      type: 'number',
                      format: 'amount',
                      value: props.appointmentFee.toString()
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {props.isDivider && (
          <div className="d-flex divider">
            <div className="line" />
          </div>
        )}

        <div className="col-md-4">
          <h2>{availableInstallationDatesLabel}</h2>
          <Calendar
            maxDate={maxDate}
            minDate={minDate}
            unAvailableDates={unAvailableDates()}
            isForAppointment
            onSelect={(date: Date) => {
              onDateSelect(date);
            }}
            selectedDate={formattedDate(selectedInstallation.date || '')}
          />
        </div>
        <div className="col-md-4" id="timeSlotsContainer">
          <h2>{availableInstallationTimeLabel}</h2>
          <RadioGroupInput
            name="timeSlot"
            id="timeSlot"
            label=""
            options={timeSlotOptions()}
            onChange={(e: any) => {
              onTimeSelect(e.target.value);
            }}
            value={selectedInstallation.timeSlot}
          />
          <div className="content-message-summary">
            {props.appointmentSelectionSummaryContent}
          </div>
        </div>
      </div>
      <div className="content-message-disclaimer">
        {props.appointmentSelectionDisclaimerContent}
      </div>
      <div className="popup-btns">
        <ActionButtons
          buttons={[
            {
              id: 'cancel',
              label: `${props.cancelBtnText}`,
              name: 'cancel',
              type: 'secondary',
              style: props.cancelBtnText === '' ? 'display: none' : '',
              onClick: props.closeAppointmentPopup
            },
            {
              id: 'schedule-install',
              label: `${scheduleInstallBtnText}`,
              name: 'schedule-install',
              type: 'primary',
              onClick: () => {
                props.scheduleInstallationAppointment(selectedInstallation);
              },
              disabled:
                (selectedInstallation.date === savedInstallationDate &&
                  selectedInstallation.timeSlot ===
                    props.savedInstallationDetails?.timeSlot) ||
                !selectedInstallation.timeSlot ||
                (props.hasTncDisclaimer && !props.tncSelected) ||
                props.isProcessing
            }
          ]}
        />
      </div>
    </div>
  );
}
